// Port of mobile src/services/storageService.ts — same canonical Storage
// paths, extended with the containers browsers record to (webm/ogg).
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  getBlob,
} from 'firebase/storage';
import {storage} from '../firebase';

const CONTENT_TYPES: Record<string, string> = {
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
  mp4: 'audio/mp4',
  webm: 'audio/webm',
  ogg: 'audio/ogg',
  pdf: 'application/pdf',
};

export const extensionOf = (path: string): string =>
  (path.split('.').pop() ?? 'm4a').toLowerCase();

export const contentTypeFor = (ext: string): string =>
  CONTENT_TYPES[ext] ?? 'audio/mp4';

export const storagePathFor = (
  uid: string,
  noteId: string,
  ext: string,
): string => `users/${uid}/notes/${noteId}/source.${ext}`;

// storage.rules caps uploads at 200MB.
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

/**
 * Uploads a note source file to its canonical Storage location. The
 * onSourceFileFinalized trigger picks it up and starts the processing
 * pipeline (queued → transcribing → summarizing → ready).
 */
export const uploadNoteFile = async (
  uid: string,
  noteId: string,
  file: Blob,
  ext: string,
  onProgress?: (percent: number) => void,
): Promise<{storagePath: string; fileSize: number}> => {
  const storagePath = storagePathFor(uid, noteId, ext);
  const task = uploadBytesResumable(ref(storage, storagePath), file, {
    contentType: contentTypeFor(ext),
  });

  return new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      snapshot => {
        if (onProgress && snapshot.totalBytes > 0) {
          onProgress(
            Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
          );
        }
      },
      reject,
      () =>
        resolve({storagePath, fileSize: task.snapshot.totalBytes}),
    );
  });
};

export const getNoteDownloadUrl = async (
  storagePath: string,
): Promise<string> => getDownloadURL(ref(storage, storagePath));

/** Downloads the note source file as a Blob (for the audio export). */
export const downloadNoteBlob = async (storagePath: string): Promise<Blob> =>
  getBlob(ref(storage, storagePath));
